import { type AuthStrategy, anonymousAuth } from './auth.ts'
import { createIdempotencyKeys, type IdempotencyKeys } from './idempotency.ts'
import {
  ApiMalformedResponseError,
  ApiProblemError,
  ApiUnauthenticatedError,
  parseProblem,
  RENEWABLE_CODES,
} from './problem.ts'
import { buildUrl, type QueryValue } from './query.ts'
import {
  type ApiRequest,
  type ApiResponse,
  type ApiTransport,
  fetchTransport,
  type HttpMethod,
} from './transport.ts'

export interface TalerClientOptions {
  readonly baseUrl: string
  readonly transport?: ApiTransport
  readonly auth?: AuthStrategy
  readonly idempotencyKeys?: IdempotencyKeys
  /** Renewal failed or was impossible: the host must run its sign-in flow. */
  readonly onSignInRequired?: (error: ApiUnauthenticatedError) => void
  readonly retry?: RetryPolicy
  readonly sleep?: (ms: number) => Promise<void>
}

export interface RetryPolicy {
  readonly attempts: number
  readonly baseDelayMs: number
}

export const DEFAULT_RETRY: RetryPolicy = { attempts: 3, baseDelayMs: 200 }

export interface CallOptions<B> {
  readonly method: HttpMethod
  readonly path: string
  readonly query?: Readonly<Record<string, QueryValue>>
  readonly body?: B
  readonly signal?: AbortSignal
  /** Overrides the default `accept` header. Only meaningful for `callRaw`. */
  readonly accept?: string
}

export interface CallMultipartOptions {
  readonly method: HttpMethod
  readonly path: string
  readonly query?: Readonly<Record<string, QueryValue>>
  readonly formData: FormData
  /** `FormData`/`File` fingerprint poorly as JSON (no enumerable own properties), so the
   * idempotency key is derived from this stand-in instead of the form data itself. */
  readonly idempotencyPayload: unknown
  readonly signal?: AbortSignal
}

export interface BinaryResponse {
  readonly blob: Blob
  readonly contentType: string
}

export interface TalerClient {
  call<T, B = undefined>(options: CallOptions<B>): Promise<T>
  /** For a non-JSON success body (e.g. a CSV export download). Still goes through auth and retry. */
  callRaw<B = undefined>(options: CallOptions<B>): Promise<ApiResponse>
  /** A `multipart/form-data` request (a file upload) - the one shape `call` cannot express. */
  callMultipart<T>(options: CallMultipartOptions): Promise<T>
  /** A binary GET response (a receipt photo) - decoded through `responseEncoding: 'base64'`
   * so the bytes survive the string-typed `ApiResponse.body` intact. */
  callBinary(
    options: Pick<CallOptions<undefined>, 'path' | 'query' | 'accept' | 'signal'>,
  ): Promise<BinaryResponse>
  readonly idempotencyKeys: IdempotencyKeys
}

const MUTATING: ReadonlySet<HttpMethod> = new Set<HttpMethod>(['POST', 'PUT', 'PATCH', 'DELETE'])

export function createTalerClient(options: TalerClientOptions): TalerClient {
  const transport = options.transport ?? fetchTransport()
  const auth = options.auth ?? anonymousAuth
  const idempotencyKeys = options.idempotencyKeys ?? createIdempotencyKeys()
  const retry = options.retry ?? DEFAULT_RETRY
  const sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)))

  async function send(request: ApiRequest): Promise<ApiResponse> {
    let last: ApiResponse | undefined
    for (let attempt = 0; attempt < retry.attempts; attempt += 1) {
      if (attempt > 0) await sleep(retry.baseDelayMs * 2 ** (attempt - 1))
      // 5xx is not stored against the idempotency key, so replaying it is safe and correct.
      last = await transport(await auth.prepare(request))
      if (last.status < 500 && !isIdempotencyInFlight(last)) return last
    }
    return last!
  }

  function idempotencyKeyFor(method: HttpMethod, path: string, query: unknown, payload: unknown): string {
    return idempotencyKeys.forPayload({ method, path, query: query ?? null, body: payload ?? null })
  }

  async function exchange(request: ApiRequest): Promise<{ request: ApiRequest; response: ApiResponse }> {
    let response = await send(request)
    let renewalFailed = false
    // Exactly one renewal per call. The retry carries the same idempotency key, so a
    // mutation that did land is replayed rather than performed twice.
    if (isRenewable(response)) {
      if (await auth.renew()) response = await send(request)
      else renewalFailed = true
    }

    const unauthenticated = classifyUnauthenticated(response, request, renewalFailed)
    if (unauthenticated !== undefined) {
      options.onSignInRequired?.(unauthenticated)
      throw unauthenticated
    }
    if (response.status >= 400) {
      const problem = parseProblem(response.body)
      if (problem === undefined) throw new ApiMalformedResponseError(request, response)
      throw new ApiProblemError(problem, request)
    }
    return { request, response }
  }

  function jsonRequest<B>(call: CallOptions<B>): ApiRequest {
    const headers: Record<string, string> = {
      accept: call.accept ?? 'application/json, application/problem+json',
    }
    const body = call.body === undefined ? undefined : JSON.stringify(call.body)
    if (body !== undefined) headers['content-type'] = 'application/json'
    if (MUTATING.has(call.method)) {
      headers['idempotency-key'] = idempotencyKeyFor(call.method, call.path, call.query, call.body)
    }
    return {
      method: call.method,
      url: buildUrl(options.baseUrl, call.path, call.query ?? {}),
      headers,
      ...(body === undefined ? {} : { body }),
      ...(call.signal === undefined ? {} : { signal: call.signal }),
    }
  }

  return {
    idempotencyKeys,
    async call<T, B>(call: CallOptions<B>): Promise<T> {
      const { request, response } = await exchange(jsonRequest(call))
      if (response.status === 204 || response.body === '') return undefined as T
      try {
        return JSON.parse(response.body) as T
      } catch {
        throw new ApiMalformedResponseError(request, response)
      }
    },
    async callRaw<B>(call: CallOptions<B>): Promise<ApiResponse> {
      const { response } = await exchange(jsonRequest(call))
      return response
    },
    async callMultipart<T>(call: CallMultipartOptions): Promise<T> {
      const headers: Record<string, string> = {
        accept: 'application/json, application/problem+json',
        'idempotency-key': idempotencyKeyFor(call.method, call.path, call.query, call.idempotencyPayload),
      }
      const request: ApiRequest = {
        method: call.method,
        url: buildUrl(options.baseUrl, call.path, call.query ?? {}),
        headers,
        body: call.formData,
        ...(call.signal === undefined ? {} : { signal: call.signal }),
      }
      const { request: sent, response } = await exchange(request)
      try {
        return JSON.parse(response.body) as T
      } catch {
        throw new ApiMalformedResponseError(sent, response)
      }
    },
    async callBinary(call): Promise<BinaryResponse> {
      const request: ApiRequest = {
        method: 'GET',
        url: buildUrl(options.baseUrl, call.path, call.query ?? {}),
        headers: {
          accept: call.accept ?? 'image/jpeg, image/png, image/webp, image/gif, application/problem+json',
        },
        responseEncoding: 'base64',
        ...(call.signal === undefined ? {} : { signal: call.signal }),
      }
      const { response } = await exchange(request)
      const contentType = response.headers['content-type'] ?? 'application/octet-stream'
      const bytes = Uint8Array.from(atob(response.body), (char) => char.charCodeAt(0))
      return { blob: new Blob([bytes], { type: contentType }), contentType }
    },
  }
}

/** A 409 from the idempotency filter is a pending state (another request with the same
 * key is still in flight), not a failure - retried the same way a 5xx is. */
function isIdempotencyInFlight(response: ApiResponse): boolean {
  if (response.status !== 409) return false
  const problem = parseProblem(response.body)
  return problem?.code === 'idempotency.in_flight'
}

function isRenewable(response: ApiResponse): boolean {
  if (response.status === 401) {
    const problem = parseProblem(response.body)
    return problem === undefined || RENEWABLE_CODES.has(problem.code)
  }
  // The contract's own 403 is always a well-formed problem+json ("authenticated, but not
  // permitted") - an unparseable 403 body is never this API's own response, only something
  // in front of it (an edge block, a misconfigured proxy) answering as if it were the API.
  // Treating it as a sign-in problem beats silently proceeding unauthenticated.
  return response.status === 403 && parseProblem(response.body) === undefined
}

function classifyUnauthenticated(
  response: ApiResponse,
  request: ApiRequest,
  renewalFailed: boolean,
): ApiUnauthenticatedError | undefined {
  if (response.status < 400 && (response.headers['content-type'] ?? '').includes('text/html')) {
    return new ApiUnauthenticatedError('not-the-api', request)
  }
  if (!isRenewable(response)) return undefined
  return new ApiUnauthenticatedError(renewalFailed ? 'renewal-failed' : 'sign-in-required', request)
}
